<template>
  <div class="flex flex-col w-full h-full bg-gray-50">
    <!-- Incomplete / setup hint -->
    <div
      v-if="isIncomplete"
      class="mx-3.5 mt-3 mb-1 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 "
    >
      <i class="pi pi-sparkles text-amber-600 text-xs flex-none" />
      <span class="text-[11.5px] text-amber-900 leading-snug">
        להפעלת העובד/ת: בחרו שירותים ושעות פעילות בלשוניות למטה, ואז שמרו.
      </span>
    </div>

    <!-- Tabs -->
    <div class="flex items-center gap-3 px-3.5 pt-3 pb-1 w-full md:max-w-2xl md:ms-8">
      <div class="flex gap-1.5 overflow-x-auto flex-1">
        <button
          v-for="t in tabs"
          :key="t.id"
          type="button"
          class="px-3.5 py-1.5 rounded-full text-[12.5px] whitespace-nowrap border transition-colors"
          :class="tab === t.id
            ? 'bg-primary text-white border-transparent font-medium'
            : 'bg-white text-gray-700 border-gray-200'"
          @click="tab = t.id"
        >
          {{ t.label }}<template v-if="t.id === 'services' && selectedServiceIds.length"> · {{ selectedServiceIds.length }}</template>
        </button>
      </div>

      <!-- Desktop save is a button in the page header, per the desktop design guideline
           (the floating SettingsSaveBar below is the mobile-only treatment). Without it
           there was no way to save services / working hours on desktop at all. -->
      <Button
        v-if="!isMobile && canEditMember && tab !== 'details'"
        label="שמירת שינויים"
        icon="pi pi-check"
        size="small"
        class="flex-none"
        :loading="saving"
        :disabled="!isDirty || saving"
        @click="save"
      />
    </div>

    <!-- Tab content -->
    <div class="flex-1 overflow-y-auto p-3.5 w-full md:max-w-2xl md:ms-8">
      <TeamDetailsTab v-if="tab === 'details'" :member="member" :can-manage="canManageOthers" />
      <TeamServicesTab
        v-else-if="tab === 'services'"
        :member="member"
        :selected="selectedServiceIds"
        :can-manage="canManageOthers"
        @toggle="toggleService"
        @set-selected="selectedServiceIds = $event"
      />
      <TeamScheduleTab
        v-else
        :working-hours="workingHours"
        :branches="branches"
        :provider-id="userId"
        :member-name="member.fullName"
        :can-manage="canManageOthers"
      />

      <!-- Save bar — slides in only when something actually changed (settings-pages pattern) -->
      <SettingsSaveBar
        v-if="canEditMember && tab !== 'details'"
        :dirty="isDirty"
        :saved="saved"
        :loading="saving"
        @save="save"
      />
    </div>

    <ConfirmDialog />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { IAvailability, Days as DaysType } from '@torim-app/types';
import { Days, MAIN_BRANCH_ID } from '@torim-app/types';
import { useToast } from 'vue-toastification';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import { useAdminAvailabilityStore, useAdminStore } from '@app/stores';
import { useSelfOrManagerPage } from '@app/composables/admin-only-guard';
import { useResponsive } from '@app/composables/responsive';
import { useAdminSettingsStore } from '@app/stores/admin-settings';
import { SettingsApi } from '@app/api/admin/settings-api';
import { ServicesApi } from '@app/api/admin/services-api';
import { AdminRouteNames } from '@app/router/route-names';
import { trackEvent } from '@app/composables/posthog';
import TeamDetailsTab from './TeamDetailsTab.vue';
import TeamServicesTab from './TeamServicesTab.vue';
import TeamScheduleTab from './TeamScheduleTab.vue';
import SettingsSaveBar from '@app/components/admin/pages/configurations/ui/SettingsSaveBar.vue';
import { servicesForProvider, teamMemberBranchIds } from './team-utils';

const route = useRoute();
const toast = useToast();
const adminStore = useAdminStore();
const settingsStore = useAdminSettingsStore();
const adminAvailabilityStore = useAdminAvailabilityStore();
const { isMobile } = useResponsive();

const DAY_ORDER: DaysType[] = [Days.SUNDAY, Days.MONDAY, Days.TUESDAY, Days.WEDNESDAY, Days.THURSDAY, Days.FRIDAY, Days.SATURDAY];

adminStore.setPage(AdminRouteNames.TeamProfile);

const userId = computed(() => String(route.params.id ?? ''));
// An employee may open their own profile (and edit their services / hours there),
// but nobody else's.
useSelfOrManagerPage(() => userId.value);

const canManageOthers = computed(() => adminStore.getCanManageOthers);
const isSelf = computed(() => userId.value === adminStore.getUserId);
// Managers edit anyone; an employee edits only themselves.
const canEditMember = computed(() => canManageOthers.value || isSelf.value);
const hasBranches = computed(() => adminStore.hasBranches);
const branches = computed(() => adminStore.getBranchSelectOptions);

const member = computed(() => {
  const client = settingsStore.getSettings?.employees?.clients?.find(c => c.userId === userId.value);
  const provider = adminStore.getProviders.find(p => p.userId === userId.value);
  return {
    userId: userId.value,
    fullName: client?.fullName ?? provider?.fullName ?? '',
    email: client?.email ?? '',
    phoneNumber: client?.phoneNumber ?? '',
    role: client?.role,
    main: client?.main ?? false,
    profileImage: provider?.profileImage,
    // Undefined while settings are still loading — the details tab waits for the real
    // value rather than assuming "allowed".
    canViewCustomerContact: client?.canViewCustomerContact,
    canViewBranchCalendar: client?.canViewBranchCalendar,
    calendarDefaults: client?.calendarDefaults,
  };
});

const tabs = [
  { id: 'details', label: 'פרטים' },
  { id: 'services', label: 'שירותים' },
  { id: 'schedule', label: 'שעות וסניפים' },
] as const;
const tab = ref<'details' | 'services' | 'schedule'>(route.query.setup ? 'services' : 'details');

// ── Services (local editable copy) ──
const selectedServiceIds = ref<string[]>([]);
const initialServiceIds = ref<string[]>([]);
const toggleService = (id: string) => {
  selectedServiceIds.value = selectedServiceIds.value.includes(id)
    ? selectedServiceIds.value.filter(x => x !== id)
    : [...selectedServiceIds.value, id];
};

// ── Schedule (local editable copy) ──
const workingHours = reactive<IAvailability>({} as IAvailability);
let initialScheduleSnapshot = '';

const toHourString = (h: unknown): string => (typeof h === 'string' ? h : (h as { name: string })?.name);
const minifySchedule = (): IAvailability =>
  Object.fromEntries(Object.entries(workingHours).map(([day, slots]) => [
    day,
    (slots ?? []).map(s => ({
      from: toHourString(s.from),
      to: toHourString(s.to),
      ...(s.branchId ? { branchId: s.branchId } : {}),
    })),
  ])) as unknown as IAvailability;

const seedFromStore = () => {
  const availability = adminAvailabilityStore.getAvailability?.[userId.value] ?? ({} as IAvailability);
  for (const day of DAY_ORDER) {
    const blocks = (availability[day] ?? []).map(b => ({
      from: toHourString(b.from),
      to: toHourString(b.to),
      branchId: hasBranches.value ? (b.branchId || MAIN_BRANCH_ID) : b.branchId,
    }));
    workingHours[day] = blocks;
  }
  initialScheduleSnapshot = JSON.stringify(minifySchedule());

  const ids = servicesForProvider(userId.value).map(s => s.id);
  selectedServiceIds.value = [...ids];
  initialServiceIds.value = [...ids];
};

// ── Dirty tracking ──
const servicesDirty = computed(() =>
  selectedServiceIds.value.length !== initialServiceIds.value.length
  || selectedServiceIds.value.some(id => !initialServiceIds.value.includes(id))
);
const scheduleDirty = computed(() => JSON.stringify(minifySchedule()) !== initialScheduleSnapshot);
const isDirty = computed(() => servicesDirty.value || scheduleDirty.value);

const isIncomplete = computed(() =>
  !member.value.main
  && (selectedServiceIds.value.length === 0 || teamMemberBranchIds(userId.value).length === 0)
);

const saving = ref(false);
const saved = ref(false);
let savedTimeout: ReturnType<typeof setTimeout> | undefined;
const save = async () => {
  if (!isDirty.value || saving.value) return;
  saving.value = true;
  trackEvent('team_member_saved');
  try {
    // Both writes mutate the same business document, so run them sequentially. Firing them in
    // parallel (Promise.all) races two load-modify-saves on the business and throws a Mongoose
    // VersionError (services vs. availability).
    if (servicesDirty.value)
      await ServicesApi.attachServicesToProvider({ servicesIds: selectedServiceIds.value, providerId: userId.value });
    if (scheduleDirty.value)
      await adminAvailabilityStore.updateBusinessAvailability(minifySchedule(), userId.value);
    // Refresh services (providingClients) so counts/completeness reflect the change.
    if (servicesDirty.value) await adminStore.fetchBusinessData();
    seedFromStore();
    // the save bar's "saved" pop replaces the old toast
    saved.value = true;
    if (savedTimeout) clearTimeout(savedTimeout);
    savedTimeout = setTimeout(() => (saved.value = false), 1800);
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לשמור את השינויים');
  } finally {
    saving.value = false;
  }
};

// Load settings + availability if not already present, then seed local state.
const ensureLoaded = async () => {
  const jobs: Promise<unknown>[] = [];
  if (!settingsStore.getSettings?.employees?.clients) jobs.push(settingsStore.fetchBusinessSettings());
  if (!Object.keys(adminAvailabilityStore.getAvailability ?? {}).length) jobs.push(adminAvailabilityStore.fetchBusinessAvailability());
  // Date-range extra hours live in their own collection — the schedule tab lists them.
  jobs.push(adminAvailabilityStore.fetchAvailabilityOverrides());
  await Promise.all(jobs).catch(console.error);
  seedFromStore();
};
ensureLoaded();

// Re-seed if navigating between members without unmounting.
watch(userId, () => ensureLoaded());
</script>
